import React, { Fragment } from 'react'

import { Stack, Typography, Box, Divider } from '@mui/material'
import { BoltOutlined, CreditCard, Pix } from '@mui/icons-material'

import { ChargingPaymentMethod, PaymentCreditCard, PaymentMethodEnum } from '@/types/Payment'

import { numericUtils } from '@/utils/numeric'
import { chargingUtils } from '@/utils/charging'
import { UseWalletBalanceInfo } from './UseWalletBalanceInfo'
import { hasSufficientWalletBalance } from '../helpers/has-sufficient-wallet-balance'

interface PaymentSummaryProps {
  amount: number
  ratePerKwh: number
  activationFee?: number
  paymentMethod: ChargingPaymentMethod | null
  selectedCard?: PaymentCreditCard
  pendingBalance?: number
  useWalletBalance: boolean | null
  balance: number
}

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  amount,
  ratePerKwh,
  activationFee = 0,
  paymentMethod,
  selectedCard,
  pendingBalance,
  useWalletBalance,
  balance,
}) => {
  const chargingAmount = pendingBalance ? Math.max(0, amount - pendingBalance) : amount
  const equivalentKwh = chargingUtils.getEquivalentKwh(chargingAmount, ratePerKwh, activationFee)

  return (
    <Box
      sx={{
        backgroundColor: 'action.hover',
        borderRadius: 2,
        padding: 2,
      }}
    >
      <Stack spacing={0.5}>
        <Stack width="100%" direction="column" alignItems="center" justifyContent="space-between">
          <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Valor total:
            </Typography>

            <Typography variant="subtitle2" fontWeight="bold">
              {useWalletBalance && !hasSufficientWalletBalance(amount, balance)
                ? numericUtils.formatNumberToBRLCurrency(Number(amount) - Number(balance))
                : numericUtils.formatNumberToBRLCurrency(amount)}
            </Typography>
          </Stack>
          {useWalletBalance && (
            <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Usar saldo da carteira:
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                {useWalletBalance ? 'Sim' : 'Não'}
              </Typography>
            </Stack>
          )}
        </Stack>

        {!pendingBalance && useWalletBalance && (
          <>
            <Stack paddingY={1.5}>
              <UseWalletBalanceInfo amount={amount} balance={balance} />
            </Stack>
            <Divider />
          </>
        )}

        {pendingBalance ? (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="warning.main">
                Taxa por ociosidade:
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                - {numericUtils.formatNumberToBRLCurrency(pendingBalance)}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Valor efetivo:
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                {numericUtils.formatNumberToBRLCurrency(chargingAmount)}
              </Typography>
            </Stack>
          </>
        ) : null}

        <Stack direction="row" alignItems="center" spacing={1.5} paddingY={1.5}>
          <BoltOutlined color="primary" sx={{ fontSize: 24 }} />
          <Stack>
            <Typography variant="body2" color="text.secondary">
              Estimativa:
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight="bold" color="primary">
                {numericUtils.formatNumberToBRLDecimal(equivalentKwh)} kWh
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({numericUtils.formatNumberToBRLCurrency(ratePerKwh)}/kWh)
              </Typography>
            </Stack>
            {activationFee > 0 && (
              <Typography variant="caption" color="text.secondary">
                inclui {numericUtils.formatNumberToBRLCurrency(activationFee)} de taxa de ativação
              </Typography>
            )}
          </Stack>
        </Stack>

        {!hasSufficientWalletBalance(amount, balance) && (
          <>
            <Divider />

            <Stack direction="row" alignItems="center" spacing={1} paddingY={1.5}>
              {paymentMethod === PaymentMethodEnum.PIX && (
                <Fragment>
                  <Pix fontSize="small" color="primary" />
                  <Typography variant="body2">Pix</Typography>
                </Fragment>
              )}
              {paymentMethod === PaymentMethodEnum.CREDIT_CARD && (
                <Fragment>
                  <CreditCard fontSize="small" color="primary" />
                  <Stack direction="column">
                    <Typography variant="body2">Cartão de Crédito</Typography>
                    {selectedCard && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedCard.brand} {selectedCard.card_mask}
                      </Typography>
                    )}
                  </Stack>
                </Fragment>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  )
}
